import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import HideTableIcon from "../../components/ShowHideTableIcon/Hide";
import ShowTableIcon from "../../components/ShowHideTableIcon/Show";
import InfoIcon from "../../components/InfoIcon";
import SortedIcon from "../../components/SortTableIcon/Sorted";
import UnsortedIcon from "../../components/SortTableIcon/Unsorted";
import TipBox from "../../components/TipBox";
import { useTableVariant } from "./TableVariantContext";

const TableHeader = React.memo(
  ({ headerGroups, toggleColumnVisibility, hiddenColumns }) => {
    const { t } = useTranslation();
    const tableVariant = useTableVariant();
    const isLovable = tableVariant === "lovable";

    const renderLovableHeaderTools = (column) => (
      <div className="lovable-header-cell__tools">
        {column.tipHeader && column.tipBody && (
          <TipBox title={column.tipHeader} message={column.tipBody}>
            <IconButton
              size="small"
              className="lovable-header-tool-btn"
              aria-label={column.tipHeader}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </TipBox>
        )}
        {!hiddenColumns.includes(column.id) && column.canGroupBy && (
          <TipBox title={column.isGrouped ? "Ungroup" : "Group"}>
            <IconButton
              size="small"
              className="lovable-header-tool-btn"
              onClick={(e) => {
                e.stopPropagation();
                column.toggleGroupBy();
              }}
              aria-label={column.isGrouped ? "Ungroup" : "Group"}
            >
              {column.isGrouped ? "📊" : "⊕"}
            </IconButton>
          </TipBox>
        )}
        {!hiddenColumns.includes(column.id) && column.canSort && (
          <TipBox title="Sort">
            <IconButton
              size="small"
              className="lovable-header-tool-btn"
              {...column.getSortByToggleProps()}
              aria-label="Sort column"
            >
              {column.isSorted ? (
                column.isSortedDesc ? (
                  <ArrowDownwardIcon />
                ) : (
                  <ArrowUpwardIcon />
                )
              ) : (
                <UnfoldMoreIcon />
              )}
            </IconButton>
          </TipBox>
        )}
        {column.canHide && (
          <TipBox
            title={
              hiddenColumns.includes(column.id) ? "Show Column" : "Hide Column"
            }
          >
            <IconButton
              size="small"
              className="lovable-header-tool-btn"
              onClick={() => toggleColumnVisibility(column.id)}
              aria-label={
                hiddenColumns.includes(column.id) ? "Show column" : "Hide column"
              }
            >
              {hiddenColumns.includes(column.id) ? (
                <VisibilityOffOutlinedIcon />
              ) : (
                <VisibilityOutlinedIcon />
              )}
            </IconButton>
          </TipBox>
        )}
      </div>
    );

    const renderColumnContent = (column) => {
      if (column.id === "expander") {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {column.render("Header")}
          </div>
        );
      }

      const headerLabel =
        typeof column.render === "function"
          ? column.render("header")
          : column.header;

      if (isLovable) {
        if (column.type === "custom") {
          return (
            <div className="lovable-header-cell lovable-header-cell--custom">
              {headerLabel}
            </div>
          );
        }
        return (
          <div className="lovable-header-cell">
            {!hiddenColumns.includes(column.id) && (
              <>
                <span className="custom-table-header-label">
                  {headerLabel}
                  {column.isGrouped ? " (Grouped)" : ""}
                </span>
                {renderLovableHeaderTools(column)}
              </>
            )}
          </div>
        );
      }

      return (
        <>
          {column.tipHeader && column.tipBody && (
            <TipBox title={column.tipHeader} message={column.tipBody}>
              <div>
                <IconButton
                  size="small"
                  style={{
                    position: "absolute",
                    top: "-17px",
                    left:
                      (column.canSort || column.canGroupBy) &&
                      !hiddenColumns.includes(column.id)
                        ? "8px"
                        : "50%",
                    transform:
                      column.canSort || column.canGroupBy
                        ? "none"
                        : "translateX(-50%)",
                  }}
                >
                  <InfoIcon />
                </IconButton>
              </div>
            </TipBox>
          )}
          {!hiddenColumns.includes(column.id) && column.canGroupBy && (
            <TipBox title={column.isGrouped ? "Ungroup" : "Group"}>
              <div>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    column.toggleGroupBy();
                  }}
                  style={{
                    position: "absolute",
                    top: "-17px",
                    right: column.canSort ? "32px" : "8px",
                  }}
                >
                  {column.isGrouped ? "📊" : "⊕"}
                </IconButton>
              </div>
            </TipBox>
          )}
          {!hiddenColumns.includes(column.id) && column.canSort && (
            <TipBox title="Sort">
              <div>
                <IconButton
                  size="small"
                  {...column.getSortByToggleProps()}
                  style={{
                    position: "absolute",
                    top: "-17px",
                    right: "8px",
                  }}
                >
                  {column.isSorted ? <SortedIcon /> : <UnsortedIcon />}
                </IconButton>
              </div>
            </TipBox>
          )}
          {column.canHide && (
            <TipBox
              title={
                hiddenColumns.includes(column.id)
                  ? "Show Column"
                  : "Hide Column"
              }
            >
              <div>
                <IconButton
                  size="small"
                  style={{
                    position: "absolute",
                    top: "-17px",
                    left:
                      !hiddenColumns.includes(column.id) && !column.tipBody
                        ? "8px"
                        : !hiddenColumns.includes(column.id) && column.tipBody
                        ? "32%"
                        : "0",
                  }}
                  onClick={() => toggleColumnVisibility(column.id)}
                >
                  {hiddenColumns.includes(column.id) ? (
                    <HideTableIcon />
                  ) : (
                    <ShowTableIcon />
                  )}
                </IconButton>
              </div>
            </TipBox>
          )}
          {!hiddenColumns.includes(column.id) && (
            <span
              className="custom-table-header-label"
              style={{
                paddingTop: isLovable ? 0 : "5px",
                overflowWrap: "anywhere",
                fontWeight: column.isGrouped ? "800" : "bold",
                color: isLovable ? undefined : "#193666",
              }}
            >
              {typeof column.render === "function"
                ? column.render("header")
                : column.header}
              {column.isGrouped ? " (Grouped)" : ""}
            </span>
          )}
        </>
      );
    };

    return (
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr
            className="customtableheader"
            {...headerGroup.getHeaderGroupProps()}
          >
            {headerGroup.headers.map((column) => (
              <th
                {...column.getHeaderProps([
                  {
                    ...column.getSortByToggleProps({ title: undefined }),
                    ...(column.getGroupByToggleProps
                      ? column.getGroupByToggleProps()
                      : {}),
                  },
                ])}
                className={[
                  column.type !== "spacer" && column.id !== "actions" && !isLovable
                    ? "td-with-background"
                    : "",
                  isLovable && column.type !== "spacer" && column.id !== "actions"
                    ? "lovable-th"
                    : "",
                  isLovable && column.type === "custom"
                    ? "lovable-th--custom"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                style={{
                  width:
                    column.type === "spacer"
                      ? column.width
                      : hiddenColumns.includes(column.id)
                      ? "30px"
                      : column.width || "auto",
                  padding:
                    column.type === "spacer"
                      ? "0"
                      : hiddenColumns.includes(column.id)
                      ? "0"
                      : isLovable
                      ? "12px"
                      : "8px",
                  textAlign: isLovable ? "left" : "center",
                  position: "relative",
                  overflow: isLovable ? "visible" : undefined,
                }}
              >
                {hiddenColumns.includes(column.id) && (
                  <div className="hidden-column-icon">
                    <TipBox
                      customStyle={{ margin: "0 auto" }}
                      title={t(column.render("header"))}
                    >
                      <IconButton
                        size="small"
                        className={isLovable ? "lovable-header-tool-btn" : undefined}
                      >
                        {isLovable ? (
                          <InfoOutlinedIcon />
                        ) : (
                          <InfoIcon />
                        )}
                      </IconButton>
                    </TipBox>
                  </div>
                )}
                <div
                  className="custom-table-header-cell-inner"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isLovable ? "flex-start" : "center",
                    height: "100%",
                  }}
                >
                  {renderColumnContent(column)}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>
    );
  }
);

TableHeader.propTypes = {
  headerGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  toggleColumnVisibility: PropTypes.func.isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default TableHeader;
